import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../app/store';
import { useGetRegistrationQuery } from '../../../services/RegistrationService';
import { CardContent, Stack, Box, Typography, Alert } from '@mui/material';
import { PersonOutline, HomeOutlined } from '@mui/icons-material';
import { AuthenticatedTemplate } from "@azure/msal-react";
import { PageContainer, PageCard, SectionHeader, SectionPaper, PageLoading as Loading } from '../../common/components';
import GoBackButton from '../../common/components/styled-ui-elements/GoBackButton';
import { FirstNameEdit, MiddleNameEdit, LastNameEdit, GenderEdit, DateOfBirthEdit, RaceEdit, MaritalStatusEdit, PrimaryPhoneEdit, EmailEdit, AlternatePhoneEdit } from '../components/fieldEditors';
import { AddressGrid } from '../components/grids';
import { SaveRegistrationButton } from '../components/commands';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const RegistrationEditContent: React.FC = () => {
  const query = useQuery();
  const company = query.get('company') || '';
  const center = query.get('center') || '';
  const mrn = query.get('mrn') || '';
  const navigate = useNavigate();
  const registration = useAppSelector((state) => state.registration.currentRegistration);

  // Create concatenated ID for API call
  const registrationId = `${company}-${center}-${mrn}`;

  // Use RTK Query to fetch registration data (extraReducers will handle loading into state)
  const { error, isLoading } = useGetRegistrationQuery({ id: registrationId }, {
    skip: !company || !center || !mrn, // Skip query if required params are missing
  });

  // Redirect to create page if required params are missing
  useEffect(() => {
    if (!company || !center || !mrn) {
      navigate('/RegistrationCreate');
      return;
    }
  }, [company, center, mrn, navigate]);

  // Show loading state
  if (isLoading) {
    return <Loading />;
  }

  // Show error state
  if (error) {
    return (
      <PageContainer>
        <Box display="flex" justifyContent="center">
          <PageCard>
            <CardContent>
              <Alert severity="error">
                Failed to load registration data. Please try again.
              </Alert>
            </CardContent>
          </PageCard>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Box display="flex" justifyContent="center">
        <PageCard>
          {/* Professional Header */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderBottom: '2px solid #3b82f6',
              p: 2.5,
              position: 'relative',
            }}
          >
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography variant="h5" component="h1" fontWeight={600} color="#1e293b" sx={{ mb: 0.5 }}>
                  Edit Patient Registration
                </Typography>
                <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.9rem' }}>
                  Update patient information - MRN: {mrn}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <SaveRegistrationButton mode="update" />
                <GoBackButton />
              </Box>
            </Box>
          </Box>
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            <form autoComplete="off">
              <Stack spacing={2.5}>
                {/* Patient Information Section */}
                <Box>
                  <SectionHeader icon={<PersonOutline sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} title="Patient Information" />
                  <SectionPaper>
                    {/* Name Fields */}
                    <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                      <FirstNameEdit />
                      <MiddleNameEdit />
                      <LastNameEdit />
                    </Stack>
                    {/* Demographics Fields */}
                    <Stack direction="row" gap={2} sx={{ mb: 2 }}>
                      <DateOfBirthEdit />
                      <GenderEdit />
                      <RaceEdit />
                      <MaritalStatusEdit />
                    </Stack>
                    {/* Contact Information Fields */}
                    <Stack direction="row" gap={2}>
                      <PrimaryPhoneEdit />
                      <EmailEdit />
                      <AlternatePhoneEdit />
                    </Stack>
                  </SectionPaper>
                </Box>
                {/* Address Information Section */}
                <Box>
                  <SectionHeader icon={<HomeOutlined sx={{ mr: 1, color: '#3b82f6', fontSize: 18 }} />} title="Address Information" />
                  <SectionPaper>
                    <AddressGrid />
                  </SectionPaper>
                </Box>
              </Stack>
            </form>
          </CardContent>
        </PageCard>
      </Box>
    </PageContainer>
  );
};

const RegistrationEdit: React.FC = () => {
  return (
    <AuthenticatedTemplate>
      <RegistrationEditContent />
    </AuthenticatedTemplate>
  );
};

export default RegistrationEdit;
